import { useCallback, useEffect, useState } from "react";
import { onSnapshot } from "firebase/firestore";
import { sessionRef, writeRoomSession } from "../firebase";

const useRoomSession = ({ roomId, participantId, isCreator }) => {
  const [sessionState, setSessionState] = useState({
    status: "idle", // idle | running | complete
    duration: 25,
    mode: "lofi",
    startedAt: null,
    paused: false,
  });
  const [error, setError] = useState(null);

  // Subscribe to session changes
  useEffect(() => {
    if (!roomId) return undefined;

    const unsubscribe = onSnapshot(
      sessionRef(roomId),
      (snapshot) => {
        if (snapshot.exists()) {
          const roomData = snapshot.data();
          const data = roomData?.session || {};
          
          if (data.status) {
            setSessionState({
              status: data.status || "idle",
              duration: data.duration || 25,
              mode: data.mode || "lofi",
              startedAt: data.startedAt?.toMillis?.() || null,
              paused: data.paused || false,
            });
          } else {
            // No session yet, stay in idle
            setSessionState((prev) => ({
              ...prev,
              status: "idle",
            }));
          }
        } else {
          // No room document yet, stay in idle
          setSessionState((prev) => ({
            ...prev,
            status: "idle",
          }));
        }
      },
      (err) => {
        console.error("[useRoomSession] Snapshot error", err);
        setError(err);
      }
    );

    return unsubscribe;
  }, [roomId]);

  // Creator-only: start session
  const startSession = useCallback(
    async ({ duration, mode }) => {
      if (!isCreator || !roomId) {
        console.warn("[useRoomSession] Only creator can start session");
        return;
      }

      try {
        await writeRoomSession({
          roomId,
          duration,
          mode,
          status: "running",
          paused: false,
        });
      } catch (err) {
        console.error("[useRoomSession] Failed to start session", err);
        setError(err);
      }
    },
    [isCreator, roomId]
  );

  // Creator-only: complete session
  const completeSession = useCallback(async () => {
    if (!isCreator || !roomId) {
      console.warn("[useRoomSession] Only creator can complete session");
      return;
    }

    try {
      await writeRoomSession({
        roomId,
        duration: sessionState.duration,
        mode: sessionState.mode,
        status: "complete",
        paused: false,
      });
    } catch (err) {
      console.error("[useRoomSession] Failed to complete session", err);
      setError(err);
    }
  }, [isCreator, roomId, sessionState.duration, sessionState.mode]);

  // Creator-only: pause/resume session
  const togglePause = useCallback(async () => {
    if (!isCreator || !roomId) {
      console.warn("[useRoomSession] Only creator can pause/resume session");
      return;
    }

    try {
      await writeRoomSession({
        roomId,
        duration: sessionState.duration,
        mode: sessionState.mode,
        status: sessionState.status,
        paused: !sessionState.paused,
      });
    } catch (err) {
      console.error("[useRoomSession] Failed to toggle pause", err);
      setError(err);
    }
  }, [isCreator, roomId, sessionState]);

  // Creator-only: restart session (from complete back to idle)
  const restartSession = useCallback(async () => {
    if (!isCreator || !roomId) {
      console.warn("[useRoomSession] Only creator can restart session");
      return;
    }

    try {
      await writeRoomSession({
        roomId,
        duration: sessionState.duration,
        mode: sessionState.mode,
        status: "idle",
        paused: false,
      });
    } catch (err) {
      console.error("[useRoomSession] Failed to restart session", err);
      setError(err);
    }
  }, [isCreator, roomId, sessionState.duration, sessionState.mode]);

  return {
    sessionState,
    startSession,
    completeSession,
    togglePause,
    restartSession,
    error,
  };
};

export default useRoomSession;

